import type { DayReflection, ReflectionImpact, ReflectionImpactScope, ReflectionImpactStatus } from '../types/reflection.js';

const REFLECTIONS_URL = '/api/reflections';

async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    credentials: 'include',
    ...init,
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(body.error?.message ?? body.error ?? `Request failed: ${response.status}`);
  }
  return response.json() as Promise<T>;
}

export async function createNote(text: string, tag?: string): Promise<DayReflection> {
  return fetchJson<DayReflection>(REFLECTIONS_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text, tag }),
  });
}

export interface GetNotesParams {
  processed?: number;
  limit?: number;
  offset?: number;
  tag?: string;
}

export async function getNotes(params?: GetNotesParams): Promise<DayReflection[]> {
  const searchParams = new URLSearchParams();
  if (params?.processed !== undefined) searchParams.set('processed', String(params.processed));
  if (params?.limit !== undefined) searchParams.set('limit', String(params.limit));
  if (params?.offset !== undefined) searchParams.set('offset', String(params.offset));
  if (params?.tag) searchParams.set('tag', params.tag);

  const query = searchParams.toString();
  return fetchJson<DayReflection[]>(`${REFLECTIONS_URL}${query ? `?${query}` : ''}`);
}

export async function getCount(): Promise<{ unprocessed: number }> {
  return fetchJson<{ unprocessed: number }>(`${REFLECTIONS_URL}/count`);
}

export async function deleteNote(id: number): Promise<void> {
  const response = await fetch(`${REFLECTIONS_URL}/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(body.error?.message ?? body.error ?? `Delete failed: ${response.status}`);
  }
}

export async function getImpacts(scope?: ReflectionImpactScope, status?: ReflectionImpactStatus): Promise<ReflectionImpact[]> {
  const searchParams = new URLSearchParams();
  if (scope) searchParams.set('scope', scope);
  if (status) searchParams.set('status', status);

  const query = searchParams.toString();
  return fetchJson<ReflectionImpact[]>(`${REFLECTIONS_URL}/impacts${query ? `?${query}` : ''}`);
}
